import { useState } from "react";
import { Search, User, Bookmark, ShoppingBag, Menu } from "lucide-react";
import { Link } from "react-router-dom";
import { useCartStore } from "@/stores/useCart";
import { useWishlist } from "@/hooks/useWishlist";

interface HeaderProps {
  onMenuOpen: () => void;
  onSearchOpen: () => void;
  onAccountOpen: () => void;
  onWishlistOpen: () => void;
  onCartOpen: () => void;
}

const Header = ({ onMenuOpen, onSearchOpen, onAccountOpen, onWishlistOpen, onCartOpen }: HeaderProps) => {
  const totalItems = useCartStore((s) => s.items.reduce((sum, i) => sum + i.quantity, 0));
  const { handles: wishlistHandles, isLoggedIn } = useWishlist();
  const wishlistCount = wishlistHandles.length;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border">
      <div
        className="mx-auto w-full flex items-center justify-between"
        style={{ height: "var(--header-height)" }}
      >
        {/* Left zone */}
        <div className="flex items-center gap-5 md:gap-6 pl-5 md:pl-8 lg:pl-10">
          <button
            onClick={onMenuOpen}
            className="text-foreground hover:text-accent transition-colors"
            aria-label="Open menu"
          >
            <Menu size={24} strokeWidth={1.4} />
          </button>
          <button
            onClick={onSearchOpen}
            className="text-foreground hover:text-accent transition-colors"
            aria-label="Search"
          >
            <Search size={22} strokeWidth={1.4} />
          </button>
        </div>

        {/* Center zone — logo */}
        <Link to="/" className="absolute left-1/2 -translate-x-1/2">
          <span className="font-sans text-[14px] tracking-[0.35em] text-accent font-medium uppercase select-none">
            MOTAI
          </span>
        </Link>

        {/* Right zone */}
        <div className="flex items-center gap-4 md:gap-5 pr-5 md:pr-8 lg:pr-10">
          <button
            onClick={onAccountOpen}
            className="text-foreground hover:text-accent transition-colors"
            aria-label="Account"
          >
            <User size={22} strokeWidth={1.4} />
          </button>
          <button
            onClick={onWishlistOpen}
            className="relative text-foreground hover:text-accent transition-colors"
            aria-label="Wishlist"
          >
            <Bookmark size={22} strokeWidth={1.4} />
            {isLoggedIn && wishlistCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center bg-accent text-accent-foreground text-[10px] leading-none rounded-full">
                {wishlistCount > 9 ? "9+" : wishlistCount}
              </span>
            )}
          </button>
          <button
            onClick={onCartOpen}
            className="relative text-foreground hover:text-accent transition-colors"
            aria-label="Cart"
          >
            <ShoppingBag size={22} strokeWidth={1.4} />
            {totalItems > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center bg-accent text-accent-foreground text-[10px] leading-none rounded-full">
                {totalItems}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
